import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ENABLE_TEXT_CONTENT_EDIT } from '../actions';
import { TOOLS_IDS } from '../utils/constants';
import dispatchTextWithCardSync from '../utils/dispatchTextWithCardSync';
import mergeTextAnnotationContentWithFit from '../utils/mergeTextAnnotationContentWithFit';
import useDebouncedCallback from './useDebouncedCallback';
import { registerTextContentFieldFocus } from './textContentFieldFocus';
import useStore from './useStore';

const useTextContentField = () => {
  const {
    selectionsIds = [],
    annotations = {},
    dispatch,
    designLayer,
    textIdOfEditableContent,
  } = useStore();

  const fieldRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textId = useMemo(() => {
    if (selectionsIds.length !== 1) {
      return null;
    }
    const selected = annotations[selectionsIds[0]];
    if (selected?.name !== TOOLS_IDS.TEXT || selected?.locked) {
      return null;
    }
    return selectionsIds[0];
  }, [annotations, selectionsIds]);

  const committedText = textId ? annotations[textId]?.text ?? '' : '';

  useEffect(() => {
    if (!textId) {
      setDraft('');
      return;
    }
    if (!isFocused) {
      setDraft(committedText);
    }
  }, [committedText, isFocused, textId]);

  const applyTextPatch = useCallback(
    (value) => {
      if (!textId) {
        return;
      }
      const base = annotations[textId];
      if (!base || base.locked) {
        return;
      }
      const nextText = String(value ?? '');
      const previousText = String(base.text ?? '');
      if (nextText === previousText) {
        return;
      }
      if (!nextText.trim() && previousText.trim()) {
        return;
      }
      const result = mergeTextAnnotationContentWithFit(
        annotations,
        base,
        value,
      );
      if (!result?.text || !String(result.text.text ?? '').trim()) {
        return;
      }
      dispatchTextWithCardSync(
        dispatch,
        annotations,
        {
          ...result.text,
          id: textId,
        },
        designLayer,
      );
    },
    [annotations, designLayer, dispatch, textId],
  );

  const applyTextPatchRef = useRef(applyTextPatch);
  applyTextPatchRef.current = applyTextPatch;

  const debouncedLiveUpdate = useDebouncedCallback((value) => {
    applyTextPatchRef.current(value);
  }, 120, []);

  const commitDraft = useCallback(
    (value = draft) => {
      applyTextPatch(value);
    },
    [applyTextPatch, draft],
  );

  useEffect(() => {
    registerTextContentFieldFocus(() => {
      fieldRef.current?.focus();
      fieldRef.current?.select?.();
    });
    return () => registerTextContentFieldFocus(null);
  }, []);

  useEffect(() => {
    if (!textId || textIdOfEditableContent !== textId) {
      return;
    }
    dispatch({
      type: ENABLE_TEXT_CONTENT_EDIT,
      payload: { textIdOfEditableContent: null },
    });
    requestAnimationFrame(() => {
      fieldRef.current?.focus();
      fieldRef.current?.select?.();
    });
  }, [dispatch, textId, textIdOfEditableContent]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (textIdOfEditableContent) {
      dispatch({
        type: ENABLE_TEXT_CONTENT_EDIT,
        payload: { textIdOfEditableContent: null },
      });
    }
  }, [dispatch, textIdOfEditableContent]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    commitDraft();
  }, [commitDraft]);

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;
      setDraft(value);
      debouncedLiveUpdate(value);
    },
    [debouncedLiveUpdate],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        // After the browser inserts a newline, fit the box immediately (no debounce wait).
        window.setTimeout(() => {
          const value = event.target?.value;
          if (value == null || !textId) {
            return;
          }
          setDraft(value);
          applyTextPatch(value);
        }, 0);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setDraft(committedText);
        fieldRef.current?.blur();
      }
    },
    [applyTextPatch, committedText, textId],
  );

  return {
    textId,
    draft,
    fieldRef,
    handleBlur,
    handleChange,
    handleFocus,
    handleKeyDown,
  };
};

export default useTextContentField;
